import React, { useState, useMemo } from "react";
import { Calculator, ArrowRight } from "lucide-react";
import { t } from "@/content/jobs";

export default function ProviderCTA({ job, lang, T }) {
  const variant = t(job.department, "en") === "Fractional Executive" ? "fractional" : "coach";
  const defaults = variant === "fractional" ? { hours: 15, rate: 200 } : { hours: 20, rate: 150 };
  const [hours, setHours] = useState(defaults.hours);
  const [rate, setRate] = useState(defaults.rate);
  const calc = useMemo(() => {
    const h = Math.max(0, Number(hours) || 0);
    const r = Math.max(0, Number(rate) || 0);
    const gross = h * r;
    const fee = Math.round(gross * 0.15);
    return { gross, fee, net: gross - fee };
  }, [hours, rate]);
  const fmt = (n) => "€" + n.toLocaleString("en-US");
  const L = {
    title: { en: "Earnings example",              fr: "Exemple de revenus" },
    hours: { en: "Hours per month",                fr: "Heures par mois" },
    rate:  { en: "Rate (€ / hour)",                fr: "Tarif (€ / heure)" },
    gross: { en: "Gross",                          fr: "Brut" },
    fee:   { en: "Platform fee (15%)",             fr: "Frais plateforme (15 %)" },
    net:   { en: "Net per month",                  fr: "Net par mois" },
    cta:   { en: "Apply to join the marketplace",  fr: "Candidater à la marketplace" },
    note:  { en: "Applications reviewed within 7 days.", fr: "Candidatures examinées sous 7 jours." }
  };

  return (
    <div style={{
      borderRadius: 24, padding: "32px 28px",
      background: `linear-gradient(135deg, ${T.primarySoft} 0%, ${T.amberSoft} 100%)`,
      border: `1px solid ${T.border}`
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
        <Calculator size={18} color={T.primary} strokeWidth={2} />
        <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: T.text, margin: 0, letterSpacing: "-0.01em" }}>
          {t(L.title, lang)}
        </h3>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.textMute, marginBottom: 6 }}>{t(L.hours, lang)}</div>
          <input type="number" min={0} max={200} value={hours} onChange={(e) => setHours(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, backgroundColor: T.bgElev,
              border: `1px solid ${T.border}`, color: T.text, outline: "none", fontSize: 14,
              fontFamily: "ui-monospace, Menlo, monospace", boxSizing: "border-box" }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.textMute, marginBottom: 6 }}>{t(L.rate, lang)}</div>
          <input type="number" min={0} step={10} value={rate} onChange={(e) => setRate(e.target.value)}
            style={{ width: "100%", padding: "9px 12px", borderRadius: 8, backgroundColor: T.bgElev,
              border: `1px solid ${T.border}`, color: T.text, outline: "none", fontSize: 14,
              fontFamily: "ui-monospace, Menlo, monospace", boxSizing: "border-box" }} />
        </div>
      </div>
      <div style={{ paddingTop: 16, paddingBottom: 16, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: T.textMute, marginBottom: 6 }}>
          <span>{t(L.gross, lang)}</span><span style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{fmt(calc.gross)}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: T.textFaint }}>
          <span>{t(L.fee, lang)}</span><span style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>−{fmt(calc.fee)}</span>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 20, paddingBottom: 24 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t(L.net, lang)}</span>
        <span style={{ fontSize: "1.75rem", fontWeight: 700, color: T.amber, fontFamily: "ui-monospace, Menlo, monospace", letterSpacing: "-0.02em" }}>
          {fmt(calc.net)}
        </span>
      </div>
      <a href={job.external_apply_url || "/apply/form"}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          width: "100%", padding: "14px 24px", borderRadius: 12,
          backgroundColor: T.primary, color: "#FFFFFF", fontWeight: 600, fontSize: 14,
          textDecoration: "none", cursor: "pointer", boxSizing: "border-box"
        }}>
        {t(L.cta, lang)}
        <ArrowRight size={15} strokeWidth={2.5} />
      </a>
      <p style={{ fontSize: 11, color: T.textFaint, textAlign: "center", marginTop: 12, marginBottom: 0 }}>
        {t(L.note, lang)}
      </p>
    </div>
  );
}
